/**
 * Ported from `prototype/assets/icons.js` (lucide stroke icons, `viewBox="0 0 24 24"`).
 * Each icon is a list of primitive shape descriptors rather than a markup string, so
 * `Icon.tsx` can build the SVG with `React.createElement` — no raw-HTML injection anywhere
 * in the shell, which is also the render-as-text rule §13 "Display-text validation holds the line without over-blocking (D57)" holds every screen to.
 */

export interface PathShape {
  readonly tag: 'path'
  readonly d: string
}
export interface CircleShape {
  readonly tag: 'circle'
  readonly cx: number
  readonly cy: number
  readonly r: number
}
export interface RectShape {
  readonly tag: 'rect'
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly rx?: number
  readonly ry?: number
}

export type IconShape = PathShape | CircleShape | RectShape

function path(d: string): PathShape {
  return { tag: 'path', d }
}
function circle(cx: number, cy: number, r: number): CircleShape {
  return { tag: 'circle', cx, cy, r }
}
function rect(x: number, y: number, width: number, height: number, rx?: number, ry?: number): RectShape {
  return rx === undefined
    ? { tag: 'rect', x, y, width, height }
    : { tag: 'rect', x, y, width, height, rx, ry: ry ?? rx }
}

export const ICON_PATHS = {
  search: [circle(11, 11, 8), path('m21 21-4.3-4.3')],
  // P15 surface 4 (D115/D118) — the Access list's two copy-members entry points.
  import: [
    path('M12 3v12'),
    path('m8 11 4 4 4-4'),
    path('M8 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4'),
  ],
  send: [
    path(
      'M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z',
    ),
    path('m21.854 2.147-10.94 10.939'),
  ],
  'layout-list': [rect(3, 3, 7, 7, 1), rect(3, 14, 7, 7, 1), path('M14 4h7M14 9h7M14 15h7M14 20h7')],
  'layout-grid': [rect(3, 3, 7, 7, 1), rect(14, 3, 7, 7, 1), rect(14, 14, 7, 7, 1), rect(3, 14, 7, 7, 1)],
  /**
   * The third member of Home's one rotating layout glyph (`AppBar.tsx`), naming the large grid —
   * one wide tile per row, which is what lucide's `layout-template` draws and what that mode is.
   *
   * It replaces `a-large-small`, which is **deleted rather than left unused**: that glyph named a
   * density knob that no longer exists as a control of its own, and an unused icon in this map is
   * one grep away from being re-adopted for a meaning it never had.
   */
  'layout-template': [rect(3, 3, 18, 7, 1), rect(3, 14, 9, 7, 1), rect(16, 14, 5, 7, 1)],
  'arrow-up-down': [path('m21 16-4 4-4-4M17 20V4M3 8l4-4 4 4M7 4v16')],
  grip: [
    circle(5, 5, 1),
    circle(12, 5, 1),
    circle(19, 5, 1),
    circle(5, 12, 1),
    circle(12, 12, 1),
    circle(19, 12, 1),
    circle(5, 19, 1),
    circle(12, 19, 1),
    circle(19, 19, 1),
  ],
  'circle-help': [circle(12, 12, 10), path('M9.09 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4M12 17h.01')],
  menu: [path('M4 6h16M4 12h16M4 18h16')],
  settings: [
    path(
      'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
    ),
    circle(12, 12, 3),
  ],
  x: [path('M18 6 6 18M6 6l12 12')],
  /** lucide `octagon-x` — the stop-sign octagon, kept as one outline path plus the two strokes. */
  'octagon-x': [
    path(
      'M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z',
    ),
    path('m15 9-6 6M9 9l6 6'),
  ],
  check: [path('M20 6 9 17l-5-5')],
  // Install's big idle-state Scan button (§7.8, D138's QR auto-start). Ported from the
  // prototype's `prototype/assets/icons.js` `scan-qr-code` hunk.
  'scan-qr-code': [
    path('M17 12v4a1 1 0 0 1-1 1h-4'),
    path('M17 3h2a2 2 0 0 1 2 2v2'),
    path('M17 8V7'),
    path('M21 17v2a2 2 0 0 1-2 2h-2'),
    path('M3 7V5a2 2 0 0 1 2-2h2'),
    path('M7 17h.01'),
    path('M7 21H5a2 2 0 0 1-2-2v-2'),
    rect(7, 7, 5, 5, 1),
  ],
  // lucide `flashlight` — the QR step's torch pill. The camera is pointed at a sticker that is
  // very often in a stairwell, a parking basement or a gate post after dark, and the torch is the
  // difference between a scan and a manual re-type of a case-sensitive six-character code.
  flashlight: [
    path('M18 6c0 2-2 2-2 4v10a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V10c0-2-2-2-2-4V2h12z'),
    path('M6 6h12'),
    path('M12 12h.01'),
  ],
  'door-open': [
    path(
      'M13 4h3a2 2 0 0 1 2 2v14M2 20h3M13 20h9M10 12v.01M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z',
    ),
  ],
  // lucide `key-round` / `lock` — R7 §2.2 (D-h): one action, one glyph, everywhere. `key-round`
  // is *open* and `lock` is *close*, on the Home plate and on all four Gate Detail halves, in
  // place of the gate-type glyph the Open plate carried before. The keyhole's filled dot is
  // drawn the way every other dot in this file is (`wifi`, `flashlight`, `door-open`) — a
  // zero-length path with a round cap — rather than as a `CircleShape`, which `Icon.tsx` strokes
  // rather than fills.
  'key-round': [
    path(
      'M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z',
    ),
    path('M16.5 7.5h.01'),
  ],
  lock: [rect(3, 11, 18, 11, 2, 2), path('M7 11V7a5 5 0 0 1 10 0v4')],
  'square-parking': [rect(3, 3, 18, 18, 2), path('M9 17V7h4a3 3 0 0 1 0 6H9')],
  car: [path('M19 17h2v-4l-2-5H7l-2 5v4h2M5 17a2 2 0 1 0 4 0M15 17a2 2 0 1 0 4 0M5 13h14')],
  phone: [
    path(
      'M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384',
    ),
  ],
  info: [circle(12, 12, 10), path('M12 16v-4M12 8h.01')],
  mail: [rect(2, 4, 20, 16, 2), path('m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7')],
  // lucide `refresh-cw` — the Update row (D137). Two arcs and two arrowheads, mirrored through
  // the centre; drawn as one path so it scales with `stroke-width` like every other icon here.
  'refresh-cw': [
    path('M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8M21 3v5h-5'),
    path('M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16M8 16H3v5'),
  ],
  /**
   * lucide `rotate-cw` / `rotate-ccw` — the app bar's refresh control, which picks by writing
   * direction (`rotate-cw` in RTL, `rotate-ccw` in LTR) so the arrowhead leads the same way the
   * text does. They are mirror images, not one icon with a transform: a `scaleX(-1)` would flip
   * the stroke's round caps and the arc's start with it.
   */
  'rotate-cw': [path('M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8'), path('M21 3v5h-5')],
  'rotate-ccw': [path('M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'), path('M3 3v5h5')],
  'chevron-right': [path('m9 18 6-6-6-6')],
  'chevron-left': [path('m15 18-6-6 6-6')],
  'log-out': [path('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9')],
  share: [path('M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13')],
  globe: [circle(12, 12, 10), path('M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20M2 12h20')],
  clock: [circle(12, 12, 10), path('M12 6v6l4 2')],
  user: [path('M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'), circle(12, 7, 4)],
  pencil: [
    path(
      'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    ),
    path('m15 5 4 4'),
  ],
  // Sourced Lucide artwork (`house` / `warehouse` / `factory` / `school`) — the four building
  // glyphs of P5's VIEW_ICONS vocabulary. `warehouse`, `factory` and `school` are verbatim;
  // `house` carries four small adjustments (roof at 10.2 not 10, door 7.6 not 8, 6 not 5.999)
  // so its ink sits in the same 3-21 box as the hand-authored five beside it. Say *adjusted*
  // rather than *unmodified* — this block is the ISC notice's own record of what came from where.
  // `house` is also `renderNameForViewIcon()`'s fallback for any stored name this shell does
  // not know.
  house: [
    path('M15 21v-7.6a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1V21'),
    path(
      'M3 10.2a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10.2V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
    ),
  ],
  warehouse: [
    path('M18 21V10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v11'),
    path(
      'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 1.132-1.803l7.95-3.974a2 2 0 0 1 1.837 0l7.948 3.974A2 2 0 0 1 22 8z',
    ),
    path('M6 13h12'),
    path('M6 17h12'),
  ],
  factory: [
    path('M12 16h.01'),
    path('M16 16h.01'),
    path(
      'M3 19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5a.5.5 0 0 0-.769-.422l-4.462 2.844A.5.5 0 0 1 15 10.5v-2a.5.5 0 0 0-.769-.422L9.77 10.922A.5.5 0 0 1 9 10.5V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z',
    ),
    path('M8 16h.01'),
  ],
  school: [
    path('M14 21v-3a2 2 0 0 0-4 0v3'),
    path('M18 4.933V21'),
    path('m4 6 7.106-3.79a2 2 0 0 1 1.788 0L20 6'),
    path('m6 11-3.52 2.147a1 1 0 0 0-.48.854V19a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a1 1 0 0 0-.48-.853L18 11'),
    path('M6 4.933V21'),
    circle(12, 9, 2),
  ],
  'hard-hat': [
    path(
      'M10 10V5a2 2 0 0 1 2-2 2 2 0 0 1 2 2v5M14 6a6 6 0 0 1 6 6v3H4v-3a6 6 0 0 1 6-6M4 15v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
    ),
  ],
  shield: [
    path(
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    ),
  ],
  // lucide `file-text` — the sidebar's Terms of use row (D190). `shield` beside it is the
  // privacy row; the two are deliberately different glyphs, because a resident scanning the
  // sidebar for one of them should not have to read both labels to tell which is which.
  'file-text': [
    path('M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z'),
    path('M14 2v4a2 2 0 0 0 2 2h4M16 13H8M16 17H8M10 9H8'),
  ],
  copy: [rect(8, 8, 14, 14, 2), path('M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2')],
  'map-pin': [
    path(
      'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
    ),
    circle(12, 10, 3),
  ],
  'message-circle': [path('M7.9 20A9 9 0 1 0 4 16.1L2 22Z')],
  ellipsis: [circle(5, 12, 1), circle(12, 12, 1), circle(19, 12, 1)],
  wifi: [
    path('M12 20h.01'),
    path('M2 8.82a15 15 0 0 1 20 0'),
    path('M5 12.859a10 10 0 0 1 14 0'),
    path('M8.5 16.429a5 5 0 0 1 7 0'),
  ],
  /**
   * lucide `bluetooth`, added by **P16** — the gate identity row's transport glyph now has a
   * second value to show (§6), and until this PRD there was only ever the one.
   *
   * It sits in P6's shared library rather than in `web/src/ble/` for the reason this file exists:
   * every icon in the shell is a shape list here, so `Icon.tsx` can build the SVG without raw
   * markup. A one-off SVG beside the screen would be the first exception to that rule, and the
   * rule is what keeps §13's render-as-text line true across every screen.
   */
  bluetooth: [path('m7 7 10 10-5 5V2l5 5L7 17')],
  smartphone: [rect(5, 2, 14, 20, 2, 2), path('M12 18h.01')],
  // P5's VIEW_ICONS vocabulary (packages/functions/members/config.ts, §7.4) — the ten names
  // `PATCH /gates/{id}/view` accepts, and the ten the picker offers, the two lists having been
  // brought back into agreement. `bollard`, `fob`, `intercom`, `handle` and `mailbox` left with
  // the enum; `side-door` was in the enum with no artwork and is drawn now.
  //
  // The first five below are hand-authored on the 24-unit grid, in the existing stroke family
  // (pure stroke — `Icon.tsx` hard-codes `strokeWidth={2}` in viewBox units, so a filled or
  // half-scale shape here would not match its neighbours). `key` is likewise hand-authored;
  // `house`, `warehouse`, `factory` and `school` are unmodified Lucide (ISC —
  // ./ISC-lucide.txt), annotated the same way `pin` and `eye` below are.
  barrier: [
    path('M2.6 20.8h7.8'),
    path('M4.6 20.4v-7M8.4 20.4v-7'),
    circle(6.5, 11, 2),
    path('M8 9.6 19.4 3.1 20.7 5.4 9.3 11.9Z'),
  ],
  'gate-icon': [
    path('M2.8 7.4v9.8M21.2 7.4v9.8'),
    path('M5.4 19.4V9.6a4.2 4.2 0 0 1 4.2-4.2h4.8a4.2 4.2 0 0 1 4.2 4.2v9.8'),
    path('M12 5.4v14'),
    path('M5.4 13.4h4.4M14.2 13.4h4.4'),
  ],
  garage: [path('M3.4 9.2h17.2'), path('M5.4 9.2V20.4h13.2V9.2'), path('M5.4 12.9h13.2M5.4 16.6h13.2')],
  door: [
    path('M7 20.6V4.4a1.4 1.4 0 0 1 1.4-1.4h6.2a1.4 1.4 0 0 1 1.4 1.4v16.2'),
    circle(13.4, 12.4, 1),
    path('M3.6 20.6h16.8'),
  ],
  'side-door': [
    path('M3 20.4V6.4a1.4 1.4 0 0 1 1.4-1.4h15.2A1.4 1.4 0 0 1 21 6.4v14'),
    path('M12.4 20.4V10.6h5.8v9.8'),
    circle(14.2, 15.6, 0.9),
    path('M2 20.8h20'),
  ],
  key: [circle(7.6, 7.6, 4.2), path('M10.6 10.6 20.5 20.5'), path('M16.4 16.4 18.6 14.2M19.2 19.2l2.2-2.2')],
  // Sourced Lucide artwork (`pin`), unlike the hand-authored block above it: it marks the
  // captured gate position in the install flow's location step, where the copy names the pin.
  pin: [
    path('M12 17v5'),
    path(
      'M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z',
    ),
  ],
  // Sourced Lucide artwork (`eye`/`eye-off`) — the gate password's show/hide toggle.
  eye: [
    path(
      'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    ),
    circle(12, 12, 3),
  ],
  'eye-off': [
    path('M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-4.446 5.143'),
    path('M14.084 14.158a3 3 0 0 1-4.242-4.242'),
    path('M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143'),
    path('m2 2 20 20'),
  ],
  // D188 — the three appearance segments, in the order the pill lays them out. Sourced Lucide
  // artwork (`sun` / `sun-moon` / `moon`): the middle one is literally the other two composited,
  // which is what makes *auto* legible as "whichever of these the phone is in" rather than as a
  // third appearance of its own.
  sun: [
    circle(12, 12, 4),
    path('M12 2v2'),
    path('m19.07 4.93-1.41 1.41'),
    path('M20 12h2'),
    path('m17.66 17.66 1.41 1.41'),
    path('M12 20v2'),
    path('m6.34 17.66-1.41 1.41'),
    path('M2 12h2'),
    path('m4.93 4.93 1.41 1.41'),
  ],
  'sun-moon': [
    path('M12 8a2.83 2.83 0 0 0 4 4 4 4 0 1 1-4-4'),
    path('M12 2v2'),
    path('M12 20v2'),
    path('m4.9 4.9 1.4 1.4'),
    path('m17.7 17.7 1.4 1.4'),
    path('M2 12h2'),
    path('M20 12h2'),
    path('m6.3 17.7-1.4 1.4'),
    path('m19.1 4.9-1.4 1.4'),
  ],
  moon: [path('M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9')],
  /**
   * Lucide's `play`, on the preferences screen's animation demo. The closed triangle rather
   * than lucide's newer rounded-hull path: every glyph in this file is stroked at the same
   * width with no fill, and the hull's own curvature is invisible at 16px while its extra
   * anchor points are not.
   */
  play: [path('M6 3l14 9-14 9Z')],
  /**
   * Lucide's `bell` and `bell-off`, on D266's app-updates pill.
   *
   * **A pair, and it has to be a pair.** The two adjacent pills already carry a glyph per segment
   * (`sun`/`sun-moon`/`moon`, `check`/`gate-icon`), so a third pill with none would read as a
   * different kind of control rather than as a third setting. And the mute half is the struck bell
   * rather than a second unrelated mark, because what tells these two apart is one stroke through
   * the same shape — the `sun-moon` argument one entry up, spent on two states instead of three.
   */
  bell: [
    path('M10.268 21a2 2 0 0 0 3.464 0'),
    path(
      'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
    ),
  ],
  'bell-off': [
    path('M10.268 21a2 2 0 0 0 3.464 0'),
    path('M17 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 .258-1.742'),
    path('m2 2 20 20'),
    path('M8.668 3.01A6 6 0 0 1 18 8c0 2.687.77 4.653 1.707 6.05'),
  ],
  /** Lucide's `trash-2` — the invite collect step's per-row remove, a bare glyph in `--red`. */
  'trash-2': [
    path('M3 6h18'),
    path('M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6'),
    path('M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2'),
    path('M10 11v6M14 11v6'),
  ],
} as const satisfies Record<string, readonly IconShape[]>

export type IconName = keyof typeof ICON_PATHS
